/**
 * ErrorBoundary — catches render/runtime errors in the page tree so a single
 * component throwing can never blank the whole app (React unmounts the tree on
 * an uncaught error, and we have no other boundary). Shows a readable message
 * + a Reload action instead of a white screen, and logs the error/stack so it's
 * visible in the console and any error reporter.
 */
import React from 'react';

interface Props { children: React.ReactNode }
interface State { error: Error | null }

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface it — this is what shows the real cause of a blank page.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught error rendering page:', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', fontFamily: "'Poppins', sans-serif" }}>
        <div style={{ maxWidth: '520px', width: '100%', background: 'var(--card-background, #fff)', border: '1px solid var(--color-gray-200, #E5E7EB)', borderRadius: '20px', padding: '28px 24px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '30px', marginBottom: '8px' }}>⚠️</div>
          <h3 style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: 600, color: 'var(--color-gray-900, #111827)' }}>Something went wrong on this page</h3>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--color-gray-500, #6B7280)', lineHeight: 1.5 }}>
            The page hit an unexpected error. Try reloading — if it keeps happening, share this message with support.
          </p>
          <pre style={{ textAlign: 'left', fontSize: '11px', color: '#991B1B', background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: '10px', padding: '10px 12px', overflow: 'auto', maxHeight: '160px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {error.message || String(error)}
          </pre>
          <button
            type="button"
            onClick={() => { this.setState({ error: null }); window.location.reload(); }}
            style={{ marginTop: '16px', padding: '9px 18px', fontSize: '13px', fontWeight: 600, fontFamily: "'Poppins', sans-serif", color: '#FFFFFF', background: '#1E3A8A', border: 'none', borderRadius: '20px', cursor: 'pointer' }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
